"use client"

import { useState } from "react"
import { Search, Filter, X } from "lucide-react"

interface StudentsFiltersProps {
  onFilterChange?: (filters: FilterState) => void
}

export interface FilterState {
  searchQuery: string
  grade: string
  careerPath: string
}

export function StudentsFilters({ onFilterChange }: StudentsFiltersProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [grade, setGrade] = useState("")
  const [careerPath, setCareerPath] = useState("")
  const [showFilters, setShowFilters] = useState(false)

  const handleFilterChange = (newSearchQuery?: string, newGrade?: string, newCareerPath?: string) => {
    const filters = {
      searchQuery: newSearchQuery !== undefined ? newSearchQuery : searchQuery,
      grade: newGrade !== undefined ? newGrade : grade,
      careerPath: newCareerPath !== undefined ? newCareerPath : careerPath,
    }

    console.log("Filters changed:", filters)
    onFilterChange?.(filters)
  }

  const clearFilters = () => {
    setSearchQuery("")
    setGrade("")
    setCareerPath("")
    onFilterChange?.({ searchQuery: "", grade: "", careerPath: "" })
  }

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search bar */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by student name and student id..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              handleFilterChange(e.target.value, undefined, undefined)
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
          />
        </div>

        {/* Filter toggle button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition"
        >
          <Filter size={18} />
          <span className="font-medium">Filters</span>
        </button>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Grade filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grade/Class</label>
              <select
                value={grade}
                onChange={(e) => {
                  setGrade(e.target.value)
                  handleFilterChange(undefined, e.target.value, undefined)
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
              >
                <option value="">All Grades</option>
                <option value="JSS 1">JSS 1</option>
                <option value="JSS 2">JSS 2</option>
                <option value="JSS 3">JSS 3</option>
                <option value="SS 1">SS 1</option>
                <option value="SS 2">SS 2</option>
                <option value="SS 3">SS 3</option>
              </select>
            </div>

            {/* Career path filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Career Path</label>
              <select
                value={careerPath}
                onChange={(e) => {
                  setCareerPath(e.target.value)
                  handleFilterChange(undefined, undefined, e.target.value)
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
              >
                <option value="">All Career Paths</option>
                <option value="Engineering">Engineering</option>
                <option value="Medicine">Medicine</option>
                <option value="Arts">Arts</option>
                <option value="Technology">Technology</option>
                <option value="Business">Business</option>
                <option value="Education">Education</option>
                <option value="Science">Science</option>
              </select>
            </div>

            {/* Clear filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 transition"
              >
                <X size={18} />
                <span className="font-medium">Clear Filters</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
